'use client';
/**
 * app/bienvenue/page.tsx — écran d'accueil PUBLIC unique (avant connexion), OGRYN
 *
 * Flux : CHOIX DU RÔLE (particulier / prestataire) → carousel concept propre au
 * rôle → connexion/inscription, écrites ici. Le rôle choisi :
 *   - conditionne les 3 slides affichés,
 *   - est écrit sur le compte (`auth_user.ogryn_role`) à l'inscription,
 *   - route vers la bonne interface (particulier → /particulier, prestataire → /prestataire).
 *
 * LE FORMULAIRE EST À NOUS (28/08). Clerk montait ici un composant à lui, qu'on
 * repeignait par-dessus avec une trentaine de variables d'apparence sans jamais
 * en maîtriser la structure. Trois champs et deux boutons ne valaient pas cette
 * dépendance — et surtout, le compte se créait chez un tiers.
 *
 * Thème sombre/emerald (langage visuel OGRYN). Pas d'emoji — SVG only.
 */

import { useEffect, useRef, useState } from 'react';
import { ConnexionTelephone } from '@/components/auth/ConnexionTelephone';
import { authClient, useCompte } from '@/lib/auth/client';
import { COOKIE_ROLE } from '@/lib/auth/telephone';
import PwaInstall from '@/components/PwaInstall';
import { SlideToggle } from '@/components/segmented';
import { Phone, Mail, KeyRound, Users } from 'lucide-react';
// LA TÊTE D'OTTO, celle du bouton de l'assistant : c'est déjà le visage de
// l'application partout ailleurs, l'écran d'entrée n'a pas à en inventer un autre.
import { OttoIcon } from '@/components/OttoChat';

type Role = 'particulier' | 'prestataire';
type Slide = { icon: React.ReactNode; title: string; desc: string };

const SLIDES: Record<Role, Slide[]> = {
  particulier: [
    {
      icon: <BoltIcon />,
      title: 'Une panne ? En 2 minutes.',
      desc: 'Décris ton problème, fixe le prix que tu veux payer. Les réparateurs autour de toi répondent.',
    },
    {
      icon: <HandshakeIcon />,
      title: 'Tu choisis, tu compares',
      desc: 'Chaque pro propose son prix et son délai. Tu vois son itinéraire, sa note, sa distance — et tu décides.',
    },
    {
      icon: <ShieldIcon />,
      title: 'Transparent, sans surprise',
      desc: 'Devis clair avant travaux. Si le pro trouve autre chose sur place, tu re-valides avant qu’il touche à quoi que ce soit.',
    },
  ],
  prestataire: [
    {
      icon: <InboxIcon />,
      title: 'Les missions viennent à toi',
      desc: 'Reçois les demandes près de ton atelier. Réponds en un geste, propose ton prix et ton délai.',
    },
    {
      icon: <TagIcon />,
      title: 'Tes prix, ton catalogue',
      desc: 'Tu fixes tes fourchettes de tarifs par prestation. Domicile ou garage, tu gardes la main sur ce que tu factures.',
    },
    {
      icon: <WalletIcon />,
      title: 'Payé, suivi, protégé',
      desc: 'Portefeuille, historique et litiges gérés par OGRYN. Tu es payé même si le devis est refusé après diagnostic.',
    },
  ],
};

// Redirection post-auth selon le rôle choisi sur les slides.
function roleRedirect(role: Role): string {
  if (role === 'particulier') return '/particulier';
  return '/prestataire'; // effet waouh : la carte des demandes d'abord, l'onboarding ensuite
}

function erreurSociale(code: string): string {
  // Un compte existe à cette adresse mais n'a jamais été confirmé : Google ne
  // s'y rattache pas, sans quoi n'importe qui ayant inscrit l'adresse d'un autre
  // partagerait son compte (lib/auth/better-auth.ts, `accountLinking`).
  if (code === 'account_not_linked') return 'Un compte existe déjà avec cette adresse. Connecte-toi avec ton adresse e-mail et ton mot de passe.';
  if (code === 'access_denied') return 'La connexion a été annulée. Tu peux réessayer.';
  if (code === 'state_mismatch' || code === 'state_not_found') return 'La connexion a expiré. Relance la connexion depuis cet écran.';
  return 'La connexion n’a pas abouti. Réessaie ou utilise un autre mode de connexion.';
}

/** Le plancher du mot de passe, aligné sur `lib/auth/better-auth.ts`. */
const MDP_MIN = 10;

const CHAMP =
  // `champ-sombre` n'habille pas le champ : elle porte la règle
  // d'auto-remplissage de globals.css, que le fond fixe de cet écran exige.
  //
  // `leading-normal` N'EST PAS COSMÉTIQUE : `text-base` de Tailwind impose une
  // hauteur de ligne de 1.5rem, et Safari iOS place alors le curseur de frappe
  // au-dessus des lettres qu'on tape — décalage bien visible sur iPad, absent
  // sur Android (signalé le 28/08). Un champ de saisie garde la hauteur de
  // ligne naturelle de sa police.
  'champ-sombre w-full rounded-2xl bg-white/[.05] border border-white/12 px-4 py-3.5 ' +
  'text-base leading-normal text-white placeholder:text-white/35 outline-none ' +
  'focus:border-emerald-500/60 focus:bg-white/[.07] transition-colors';

/**
 * De quel côté tombe le formulaire en paysage. Une media query `landscape`
 * ne sait pas dire LEQUEL des deux paysages : les deux rotations donnent la
 * même largeur. Seul l'angle les sépare (90° d'un côté, 270° de l'autre), et
 * l'écran se retourne avec le téléphone — ce qui était à droite passe à
 * gauche, le contenu reste donc du côté où la main le tient.
 */
function usePaysageInverse() {
  const [inverse, setInverse] = useState(false);
  useEffect(() => {
    const o = window.screen?.orientation;
    if (!o) return;
    const maj = () => setInverse(o.angle === 270);
    maj();
    o.addEventListener('change', maj);
    return () => o.removeEventListener('change', maj);
  }, []);
  return inverse;
}

export default function BienvenuePage() {
  const [stage, setStage] = useState<'role' | 'intro' | 'auth'>('role');
  const [role, setRole] = useState<Role>('particulier');
  const [mode, setMode] = useState<'signin' | 'signup' | 'oubli' | 'reinit'>('signup');
  const [i, setI] = useState(0);
  const [erreurRetour, setErreurRetour] = useState<string | null>(null);
  const paysageInverse = usePaysageInverse();

  // Retour du lien de réinitialisation reçu par e-mail : Better Auth redirige
  // ici avec `?token=...` une fois le jeton vérifié. On saute direct sur le
  // formulaire, sans repasser par le choix du rôle (l'usager arrive d'une
  // boîte mail, pas d'un premier contact avec l'app).
  // `set-state-in-effect` est neutralisee ici a dessein : la source de verite
  // est l'URL, une API de plateforme absente du rendu serveur. Un initialiseur
  // paresseux ferait diverger l'hydratation (serveur `role`, client `auth`).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roleRetour = params.get('role');
    // Synchronisation initiale avec l'URL après hydratation (absente au SSR).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (roleRetour === 'prestataire' || roleRetour === 'particulier') setRole(roleRetour);
    if (params.has('error') || params.has('auth') || roleRetour) {
      setStage('auth'); setMode('signin');
      if (params.has('error')) setErreurRetour(erreurSociale(params.get('error') ?? ''));
    }
    if (params.get('token')) {
      setStage('auth'); setMode('reinit');
    }
  }, []);

  // DÈS QUE LA SESSION EXISTE, ON PART. Le rôle est déjà écrit sur le compte —
  // le formulaire d'inscription l'emporte avec lui — donc il n'y a plus rien à
  // reposer après coup. C'est ce que lira `enforceZone` pour créer le bon profil.
  const { isLoaded, isSignedIn, user } = useCompte();
  const claimed = useRef(false);
  useEffect(() => {
    if (!isSignedIn || !user || claimed.current) return;
    claimed.current = true;

    // Déjà connecté (onglet rouvert, /bienvenue en favori) : on ne repropose
    // JAMAIS le choix du rôle. Le compte a déjà le sien ; le redemander
    // enfermait dans une boucle « choix → connexion → choix » sans issue (cas
    // vécu 27/07). Le rôle du compte fait foi.
    // RETOUR D'UN FOURNISSEUR SOCIAL : le compte vient d'être créé par Apple ou
    // Google, sans passer par notre formulaire, donc sans rôle. Celui qu'on a
    // emporté dans l'adresse de retour prend le relais — mais UNIQUEMENT dans ce
    // trou-là : un compte qui porte déjà un rôle n'est jamais réécrit, sinon un
    // simple aller-retour sur l'écran de choix ferait changer de zone.
    const depuisUrl = new URLSearchParams(window.location.search).get('role');
    const reclame = !user.role && (depuisUrl === 'prestataire' || depuisUrl === 'particulier')
      ? depuisUrl : null;

    const known = reclame ?? (user.role === 'prestataire' ? 'prestataire' : 'particulier');

    (async () => {
      // L'écriture du rôle ne doit jamais empêcher de partir : si elle échoue,
      // on redirige quand même (sinon écran figé, connecté, sans issue).
      if (reclame) {
        try {
          await authClient.updateUser({
            ogrynRole: reclame === 'prestataire' ? 'prestataire' : 'client',
          } as Parameters<typeof authClient.updateUser>[0]);
        } catch { /* le profil créé à la première visite fera foi */ }
      }
      // Admin reconnu par allowlist e-mail (lib/auth/session.ts) : il passe
      // TOUJOURS par /admin, quel que soit le rôle du compte. Aucun bouton
      // admin n'existe sur cet écran — c'est le seul aiguillage possible.
      let target = roleRedirect(known);
      try {
        const res = await fetch('/api/me/is-admin');
        const { isAdmin } = await res.json() as { isAdmin: boolean };
        if (isAdmin) target = '/admin';
      } catch { /* pas admin par défaut si la vérification échoue */ }
      // Navigation DURE : /bienvenue et les deux espaces sont des écrans lourds
      // (MapLibre de l'autre côté). Le router.replace de Next restait sans effet
      // ici — l'utilisateur connecté ne bougeait plus de /bienvenue (vérifié
      // 27/07). Même remède que le garde de zone, cf. HardRedirect.tsx.
      window.location.replace(target);
    })();
  }, [isSignedIn, user]);

  const slides = SLIDES[role];

  const chooseRole = (r: Role) => { setRole(r); setI(0); setStage('intro'); };
  const openAuth = (m: 'signin' | 'signup') => { setMode(m); setStage('auth'); };

  // Swipe tactile entre les slides du carousel.
  const startX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) setI(v => Math.min(slides.length - 1, v + 1));
    else setI(v => Math.max(0, v - 1));
  };

  // Connecté (ou statut Clerk encore en cours de résolution après le saut
  // post-signup) : écran d'attente le temps du saut vers l'espace du compte.
  // Surtout pas le choix du rôle — sans le `!isLoaded`, le remount de
  // /bienvenue après SignUp affichait le choix pendant la fraction de seconde
  // où isSignedIn valait encore `undefined` (flash constaté 30/07). Placé
  // APRÈS tous les hooks (un retour anticipé au milieu en casserait l'ordre).
  if (!isLoaded || isSignedIn) {
    return (
      <div className="fixed inset-0 bg-[#2D2D2D] flex items-center justify-center">
        <span className="w-14 h-14 rounded-2xl bg-emerald-500/12 border border-emerald-500/35 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-[#2D2D2D] text-white select-none">
      <PwaInstall />
      {/* En-tête : logo + action contextuelle */}
      <div className="shrink-0 flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+16px)]">
        {stage !== 'role' ? (
          <button
            type="button"
            onClick={() => setStage(stage === 'auth' ? 'intro' : 'role')}
            /* La bosse du reste de l'application (22/09) : même rond de 36 px
               que la croix de fermeture, la flèche dedans. Cet écran est
               toujours sombre, hors du cycle jour/nuit, donc le jeton de
               relief de nuit est reposé ici en local — même procédé que la
               barre d'onglets plus bas. */
            className="emboss relative w-9 h-9 -ml-1 rounded-full flex items-center justify-center active:scale-95 transition"
            style={{
              ['--sh-emboss' as unknown as string]: '3px 3px 8px rgba(0,0,0,0.55), -3px -3px 8px rgba(255,255,255,0.10)',
            } as React.CSSProperties}
            aria-label="Retour"
          >
            <span className="relative">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </span>
          </button>
        ) : (
          <span className="text-lg font-black tracking-tight">OG<span className="text-emerald-400">RYN</span></span>
        )}
      </div>

      {stage === 'role' ? (
        /* ── Choix du rôle (tout premier écran) ── */
        /* En paysage l'écran fait ~360 px de haut : deux cartes de 96 px empilées
           sous un titre de 100 px sortaient du cadre, et le conteneur `flex-1`
           n'était pas défilable — le bouton prestataire devenait inatteignable
           (constaté sur Tecno Spark, 05/08). Les cartes passent donc côte à côte
           et les respirations se resserrent ; `overflow-y-auto` reste le filet
           pour les écrans encore plus bas. */
        <div className="flex-1 overflow-y-auto flex flex-col justify-center px-6 py-4 pb-[calc(env(safe-area-inset-bottom)+24px)]" style={{ animation: 'ogfade .32s ease-out' }}>
          <div className="text-center mb-10 landscape:mb-5">
            <h1 className="text-3xl landscape:text-2xl font-black leading-tight text-white">Bienvenue sur OGRYN</h1>
            <p className="text-base text-white/55 mt-3 landscape:mt-1.5">Vous êtes…</p>
          </div>
          <div className="space-y-4 max-w-sm landscape:space-y-0 landscape:flex landscape:gap-4 landscape:max-w-3xl w-full mx-auto">
            <button type="button" onClick={() => chooseRole('particulier')}
              className="w-full landscape:flex-1 flex items-center gap-4 landscape:gap-3 p-5 landscape:p-4 rounded-2xl bg-white/[.04] border border-white/12 hover:border-emerald-500/50 hover:bg-emerald-500/[.06] active:scale-[.99] transition-all text-left">
              <span className="w-14 h-14 landscape:w-11 landscape:h-11 rounded-2xl bg-emerald-500/12 text-emerald-400 flex items-center justify-center shrink-0"><CarIcon /></span>
              <span className="min-w-0">
                <span className="block font-black text-lg">Un particulier</span>
                <span className="block text-sm text-white/50 mt-0.5">Je cherche à faire réparer mon véhicule</span>
              </span>
            </button>
            <button type="button" onClick={() => chooseRole('prestataire')}
              className="w-full landscape:flex-1 flex items-center gap-4 landscape:gap-3 p-5 landscape:p-4 rounded-2xl bg-white/[.04] border border-white/12 hover:border-emerald-500/50 hover:bg-emerald-500/[.06] active:scale-[.99] transition-all text-left">
              <span className="w-14 h-14 landscape:w-11 landscape:h-11 rounded-2xl bg-emerald-500/12 text-emerald-400 flex items-center justify-center shrink-0"><WrenchIcon /></span>
              <span className="min-w-0">
                <span className="block font-black text-lg">Un prestataire</span>
                <span className="block text-sm text-white/50 mt-0.5">Garagiste ou mécano, je propose mes services</span>
              </span>
            </button>
          </div>
        </div>
      ) : stage === 'intro' ? (
        <>
          {/* Carousel — track horizontal (slides propres au rôle) */}
          <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="flex-1 overflow-hidden touch-pan-y">
            <div className="flex h-full transition-transform duration-300 ease-out" style={{ transform: `translateX(-${i * 100}%)` }}>
              {slides.map((s, k) => (
                <div key={k} className="w-full shrink-0 flex flex-col items-center justify-center text-center px-6">
                  <span className="w-24 h-24 rounded-[28px] bg-emerald-500/12 text-emerald-400 flex items-center justify-center mb-8">{s.icon}</span>
                  <h1 className="text-3xl font-black leading-tight max-w-xs text-white">{s.title}</h1>
                  <p className="text-base text-white/55 mt-4 leading-relaxed max-w-sm">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Dots */}
          <div className="shrink-0 flex items-center justify-center gap-2 pb-6">
            {slides.map((_, k) => (
              <button key={k} type="button" onClick={() => setI(k)} aria-label={`Slide ${k + 1}`}
                className={`h-2 rounded-full transition-all ${k === i ? 'w-6 bg-emerald-500' : 'w-2 bg-white/20'}`} />
            ))}
          </div>

          {/* CTA bas — toujours les deux actions : les slides se parcourent au swipe/dots */}
          <div className="shrink-0 p-5 pb-[calc(env(safe-area-inset-bottom)+20px)] border-t border-white/8 space-y-3">
            <button type="button" onClick={() => openAuth('signup')}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black py-4 rounded-2xl text-base transition-colors">
              Créer un compte {role === 'prestataire' ? 'prestataire' : ''}
            </button>
            <button type="button" onClick={() => openAuth('signin')}
              className="w-full bg-white/8 hover:bg-white/12 border border-white/12 text-white font-bold py-4 rounded-2xl text-base transition-colors">
              Se connecter
            </button>
          </div>
        </>
      ) : mode === 'signin' ? (
        /* Vue connexion — titre et barre d'onglets FIXES, seul le panneau
           défile entre les deux (signalé le 22/09 : le passage de la barre
           sous le panneau, seul, ne suffisait pas — my-auto recentrait tout
           le bloc à chaque changement de hauteur du panneau, donc titre et
           barre bougeaient encore avec lui). Pas de `overflow-y-auto` ici :
           le scroll est celui du panneau (`FormulaireConnexion`), pas de ce
           conteneur — titre (shrink-0) et lien du bas (shrink-0) gardent
           donc une position stable quel que soit l'onglet actif.
           `pb` de +100px (22/09) : la barre d'onglets ferme ce bloc, la coller
           au bord bas la mettait hors de portée du pouce. Le padding décolle
           la barre ET le lien « Créer un compte » du bas de l'écran. En
           paysage la hauteur manque, la barre redescend donc près du bord.

           PAYSAGE = DEUX COLONNES (22/09) : titre d'un côté, connexion de
           l'autre, au lieu d'un seul bloc écrasé sur 400px de haut. Le côté
           SUIT LA ROTATION (`paysageInverse`) — le CSS seul ne distingue pas
           les deux paysages, il faut l'angle. */
        <div key="signin" className="flex-1 min-h-0 flex flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+100px)] landscape:pb-[calc(env(safe-area-inset-bottom)+16px)]" style={{ animation: 'ogfade .32s ease-out' }}>
         <div className={`w-full max-w-sm mx-auto mt-3 flex-1 min-h-0 flex flex-col landscape:max-w-3xl landscape:gap-7 ${
           paysageInverse ? 'landscape:flex-row-reverse' : 'landscape:flex-row'}`}>
          <div className="shrink-0 pt-2 pb-8 text-center landscape:flex-1 landscape:min-w-0 landscape:flex landscape:flex-col landscape:items-center landscape:justify-center landscape:py-0">
            <span className="inline-flex w-14 h-14 mb-3 rounded-2xl bg-emerald-500/12 border border-emerald-500/25 text-emerald-400 items-center justify-center">
              <OttoIcon size={30} />
            </span>
            <h1 className="text-2xl font-black text-white">Bon retour</h1>
            <p className="text-sm text-white/50 mt-1">Connecte-toi pour continuer.</p>
            <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-emerald-300 bg-emerald-500/12 px-3 py-1 rounded-full">
              {role === 'prestataire' ? <WrenchIcon small /> : <CarIcon small />}
              Espace {role === 'prestataire' ? 'prestataire' : 'particulier'}
            </span>
          </div>

          {/* fallbackRedirectUrl reste sur /bienvenue : c'est l'effet ci-dessus qui
              redirige, une fois le rôle écrit. Partir directement vers la zone
              laissait la course se jouer et le rôle se perdre. */}
          {/* La colonne de connexion : panneau extensible puis barre fixe.
              En portrait elle occupe simplement la place restante sous le
              titre ; en paysage elle devient la seconde colonne. */}
          <div className="flex-1 min-h-0 flex flex-col landscape:min-w-0">
            {erreurRetour && <p role="alert" className="shrink-0 mb-4 rounded-xl border border-rose-300/30 bg-rose-300/10 p-3 text-sm text-rose-200">{erreurRetour}</p>}

            <FormulaireConnexion role={role} onForgot={() => setMode('oubli')} />

            <div className="shrink-0 text-center text-sm text-white/45 mt-4">
              Pas encore de compte ?{' '}
              <button type="button" onClick={() => setMode('signup')} className="font-bold text-emerald-400 hover:text-emerald-300">Créer un compte</button>
            </div>
          </div>
         </div>
        </div>
      ) : (
        /* Vue auth (inscription / mot de passe oublié / réinitialisation) —
           Clerk inline, aucun changement d'URL. Rôle mémorisé + redirection
           rôle-consciente. CENTRÉ DANS LA HAUTEUR, pas collé sous l'en-tête :
           le formulaire est court, et posé en haut il laissait un grand vide
           sous lui — l'écran paraissait tronqué (signalé le 28/08).
           `justify-center` avec `my-auto` sur le contenu : il se centre
           quand il y a de la place, et redevient défilable dès qu'il n'y en
           a plus (petit écran, clavier ouvert), au lieu d'être coupé. Ces
           vues n'ont pas de barre d'onglets à garder fixe, `my-auto` reste
           donc la bonne recette ici (contrairement à signin, cf. branche
           ci-dessus). */
        <div key={mode} className="flex-1 min-h-0 overflow-y-auto flex flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+24px)]" style={{ animation: 'ogfade .32s ease-out' }}>
         <div className="w-full max-w-sm mx-auto my-auto">
          <div className="pt-2 pb-4 text-center">
            <span className="inline-flex w-14 h-14 mb-3 rounded-2xl bg-emerald-500/12 border border-emerald-500/25 text-emerald-400 items-center justify-center">
              <OttoIcon size={30} />
            </span>
            <h1 className="text-2xl font-black text-white">
              {mode === 'signup' ? 'Crée ton compte'
                : mode === 'oubli' ? 'Mot de passe oublié'
                : 'Nouveau mot de passe'}
            </h1>
            <p className="text-sm text-white/50 mt-1">
              {mode === 'signup' ? '30 secondes, pas plus.'
                : mode === 'oubli' ? 'On t’envoie un lien par e-mail.'
                : 'Choisis-en un nouveau, dix caractères minimum.'}
            </p>
            {mode === 'signup' && (
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-emerald-300 bg-emerald-500/12 px-3 py-1 rounded-full">
                {role === 'prestataire' ? <WrenchIcon small /> : <CarIcon small />}
                Espace {role === 'prestataire' ? 'prestataire' : 'particulier'}
              </span>
            )}
          </div>

          {erreurRetour && <p role="alert" className="mb-4 rounded-xl border border-rose-300/30 bg-rose-300/10 p-3 text-sm text-rose-200">{erreurRetour}</p>}
          {mode === 'signup' ? (
            <FormulaireInscription role={role} />
          ) : mode === 'oubli' ? (
            <FormulaireOubli onBack={() => setMode('signin')} />
          ) : (
            <FormulaireReinitialisation onDone={() => setMode('signin')} />
          )}

          <div className="text-center text-sm text-white/45 mt-4">
            {mode === 'signup' ? (
              <>Déjà un compte ?{' '}
                <button type="button" onClick={() => setMode('signin')} className="font-bold text-emerald-400 hover:text-emerald-300">Se connecter</button>
              </>
            ) : (
              <button type="button" onClick={() => setMode('signin')} className="font-bold text-emerald-400 hover:text-emerald-300">Retour à la connexion</button>
            )}
          </div>
         </div>
        </div>
      )}

      <style>{`@keyframes ogfade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}

/**
 * Le champ mot de passe, avec l'œil qui le révèle.
 *
 * Un mot de passe tapé à l'aveugle sur un téléphone se trompe, et l'erreur ne
 * se voit qu'au refus. Le bouton est DANS le champ, à droite : c'est là que
 * tous les claviers mobiles l'ont mis, et l'utilisateur n'a pas à le chercher.
 *
 * `type` bascule, l'`autoComplete` ne bouge pas : le gestionnaire de mots de
 * passe doit continuer de reconnaître le champ même quand il est en clair.
 */
function ChampMotDePasse({
  value, onChange, autoComplete, placeholder = 'Mot de passe', minLength,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'current-password' | 'new-password';
  placeholder?: string;
  minLength?: number;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        className={`${CHAMP} pr-12`}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        required
        minLength={minLength}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        /* INDISPENSABLE DÈS QUE LE CHAMP PASSE EN CLAIR : un `type="password"`
           échappe à la correction du clavier, un `type="text"` non — et l'œil
           fait basculer de l'un à l'autre. Sans ça, iOS met une majuscule au
           premier caractère dès que l'utilisateur montre son mot de passe. */
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        aria-pressed={visible}
        // `tabIndex={-1}` : la tabulation doit aller du mot de passe au bouton
        // d'envoi, pas s'arrêter sur un confort d'affichage.
        tabIndex={-1}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-xl text-white/45 hover:text-white/80 active:scale-95 transition"
      >
        {visible ? <OeilBarreIcon /> : <OeilIcon />}
      </button>
    </div>
  );
}

function OeilIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function OeilBarreIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.6 6.2A9.9 9.9 0 0 1 12 6c6.4 0 10 6 10 6a17.6 17.6 0 0 1-3.2 3.9M6.6 6.6A17.6 17.6 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 4.6-1.1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M2 2l20 20" />
    </svg>
  );
}

// ── Fournisseurs sociaux ─────────────────────────────────────────────────────

/**
 * Les fournisseurs réellement configurés côté serveur, listés dans
 * `NEXT_PUBLIC_AUTH_SOCIAL` (ex. « google,apple »). Vide = aucun bouton.
 * Mieux vaut pas de bouton qu'un bouton qui mène à une page d'erreur.
 */
const SOCIAUX = (process.env.NEXT_PUBLIC_AUTH_SOCIAL ?? '')
  .split(',').map(p => p.trim()).filter(Boolean);

function BoutonsSociaux({ role, sansSeparateur }: { role: Role; sansSeparateur?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!SOCIAUX.length) return null;

  /**
   * LE RÔLE VOYAGE DANS L'ADRESSE DE RETOUR. Un départ chez Apple ou Google est
   * une navigation de premier niveau : au retour la page est rechargée à neuf et
   * tout l'état React est perdu — le rôle cliqué avec lui. C'est exactement ce
   * qui envoyait un futur prestataire dans l'espace particulier (cas vécu le
   * 27/07). L'adresse de retour, elle, traverse le voyage.
   */
  const partir = async (provider: string) => {
    if (busy) return;
    setBusy(true); setErr(null);
    document.cookie = `${COOKIE_ROLE}=${role === 'prestataire' ? 'prestataire' : 'client'}; path=/; max-age=900; samesite=lax; secure`;
    try {
      const { error } = await authClient.signIn.social({
        provider: provider as 'google' | 'apple' | 'microsoft' | 'facebook',
        callbackURL: `/bienvenue?auth=1&role=${role}`,
        errorCallbackURL: `/bienvenue?auth=1&role=${role}`,
      });
      if (error) throw new Error(error.code);
    } catch {
      setErr('Connexion indisponible pour le moment. Réessaie ou utilise ton téléphone ou ton adresse e-mail.');
      setBusy(false);
    }
  };

  return (
    <>
      <fieldset disabled={busy} className="space-y-[clamp(0.5rem,2svh,0.75rem)] disabled:opacity-60" aria-busy={busy}>
        {SOCIAUX.includes('apple') && (
          <button type="button" onClick={() => partir('apple')}
                  className="w-full flex items-center justify-center gap-2.5 bg-white text-black font-bold py-[clamp(0.55rem,1.8svh,0.875rem)] rounded-2xl text-[15px] active:scale-[.99] transition">
            <AppleIcon /> Continuer avec Apple
          </button>
        )}
        {SOCIAUX.includes('google') && (
          <button type="button" onClick={() => partir('google')}
                  className="w-full flex items-center justify-center gap-2.5 bg-white/95 text-black font-bold py-[clamp(0.55rem,1.8svh,0.875rem)] rounded-2xl text-[15px] active:scale-[.99] transition">
            <GoogleIcon /> Continuer avec Google
          </button>
        )}
        {SOCIAUX.includes('microsoft') && (
          <button type="button" onClick={() => partir('microsoft')}
                  className="w-full flex items-center justify-center gap-2.5 bg-white/95 text-black font-bold py-[clamp(0.55rem,1.8svh,0.875rem)] rounded-2xl text-[15px] active:scale-[.99] transition">
            <MicrosoftIcon /> Continuer avec Microsoft
          </button>
        )}
        {SOCIAUX.includes('facebook') && (
          <button type="button" onClick={() => partir('facebook')}
                  className="w-full flex items-center justify-center gap-2.5 bg-[#1877F2] text-white font-bold py-[clamp(0.55rem,1.8svh,0.875rem)] rounded-2xl text-[15px] active:scale-[.99] transition">
            <FacebookIcon /> Continuer avec Facebook
          </button>
        )}
      </fieldset>
      {err && <p role="alert" className="text-sm text-rose-300 px-1">{err}</p>}
      {!sansSeparateur && (
        <div className="flex items-center gap-3 py-1">
          <span className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-white/35">ou</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
      )}
    </>
  );
}

function AppleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16.4 12.8c0-2.5 2-3.7 2.1-3.8-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.8-1.7 0-3.2 1-4.1 2.5-1.7 3-.4 7.5 1.3 10 .8 1.2 1.8 2.5 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.4-1-2.4-3.6zM14 5.4c.7-.8 1.1-2 1-3.2-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3.1 1.1.1 2.2-.6 2.9-1.4z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden>
      <path fill="#F25022" d="M1 1h10v10H1z" /><path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" /><path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12Z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden>
      <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-4H24v7.6h12c-.2 2-1.5 4.9-4.4 6.9l6.8 5.3C42.4 36.2 45 30.6 45 24z" />
      <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.8-5.3c-1.9 1.3-4.4 2.2-7.7 2.2-5.9 0-10.9-3.9-12.7-9.2l-7 5.4C7.9 41 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.3 28.4c-.5-1.4-.7-2.9-.7-4.4s.3-3 .7-4.4l-7-5.4C2.8 17.1 2 20.4 2 24s.8 6.9 2.3 9.8l7-5.4z" />
      <path fill="#EA4335" d="M24 10.5c3.3 0 5.6 1.4 6.9 2.6l5.9-5.8C33.2 4 28.9 2 24 2 15.4 2 7.9 7 4.3 14.2l7 5.4c1.8-5.3 6.8-9.1 12.7-9.1z" />
    </svg>
  );
}

// ── Formulaires ──────────────────────────────────────────────────────────────

/** Le message que l'utilisateur doit lire, jamais la trace technique. */
function messageErreur(e: unknown, defaut: string): string {
  const code = (e as { error?: { code?: string; message?: string } })?.error;
  switch (code?.code) {
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'Un compte existe déjà avec cette adresse. Connecte-toi.';
    case 'INVALID_EMAIL_OR_PASSWORD':
      return 'Adresse ou mot de passe incorrect.';
    case 'PASSWORD_TOO_SHORT':
      return `Le mot de passe doit faire au moins ${MDP_MIN} caractères.`;
    default:
      return code?.message ?? defaut;
  }
}

function FormulaireInscription({ role }: { role: Role }) {
  const [parTelephone, setParTelephone] = useState(true);
  return (
    <div className="space-y-3">
      <BoutonsSociaux role={role} />
      <div className="flex gap-2" aria-label="Mode d’inscription">
        <button type="button" aria-pressed={parTelephone} onClick={() => setParTelephone(true)} className={`flex-1 rounded-xl border py-3 text-sm font-bold ${parTelephone ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10' : 'border-white/15 text-white/65'}`}>Par SMS</button>
        <button type="button" aria-pressed={!parTelephone} onClick={() => setParTelephone(false)} className={`flex-1 rounded-xl border py-3 text-sm font-bold ${!parTelephone ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10' : 'border-white/15 text-white/65'}`}>Par e-mail</button>
      </div>
      {parTelephone ? <ConnexionTelephone role={role === 'prestataire' ? 'prestataire' : 'client'} /> : <InscriptionEmail role={role} />}
    </div>
  );
}

function InscriptionEmail({ role }: { role: Role }) {
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [mdp, setMdp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // FAUX au départ, et cela ne se discute pas : une case pré-cochée ne
  // recueille pas un consentement, elle en fabrique un (art. 4.11 RGPD, et
  // l'arrêt Planet49 de la CJUE). `confidentialite.html` §2.4 le promet
  // explicitement — « une case à cocher dédiée et non pré-cochée ».
  const [marketing, setMarketing] = useState(false);

  const trop_court = mdp.length > 0 && mdp.length < MDP_MIN;

  async function envoyer(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      // LE RÔLE PART AVEC L'INSCRIPTION, pas après. C'est lui qui décide du
      // profil créé dans la foulée, côté serveur (`databaseHooks`).
      const { error } = await authClient.signUp.email({
        name: nom.trim(), email: email.trim(), password: mdp,
        ogrynRole: role === 'prestataire' ? 'prestataire' : 'client',
        // Le consentement marketing suit le même chemin que le rôle : il est
        // écrit dans la transaction qui crée le compte, pas par un appel
        // séparé qui pourrait échouer et le perdre en silence.
        ogrynMarketingConsent: marketing,
      } as Parameters<typeof authClient.signUp.email>[0]);
      if (error) throw { error };
      // La session est ouverte (`autoSignIn`) : l'effet de la page prend le
      // relais et emmène vers la bonne zone.
    } catch (e2) {
      setErr(messageErreur(e2, 'Inscription impossible — réessaie.'));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={envoyer} className="space-y-3">
      <input className={CHAMP} type="text" autoComplete="name" required
             autoCapitalize="words" autoCorrect="off"
             placeholder="Prénom et nom" value={nom} onChange={e => setNom(e.target.value)} />
      <input className={CHAMP} type="email" autoComplete="email" required
             inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
             placeholder="Adresse e-mail" value={email}
             /* Le clavier mobile glisse volontiers une espace après une
                suggestion : on la retire à la saisie plutôt que de refuser
                la connexion pour un caractère invisible. */
             onChange={e => setEmail(e.target.value.replace(/\s/g, ""))} />
      <div>
        <ChampMotDePasse value={mdp} onChange={setMdp}
                         autoComplete="new-password" minLength={MDP_MIN} />
        <p className={`text-xs mt-1.5 px-1 ${trop_court ? 'text-amber-300' : 'text-white/40'}`}>
          {MDP_MIN} caractères minimum.
        </p>
      </div>

      {/*
        SÉPARÉE DES CGU, volontairement. Un consentement groupé — « j'accepte
        les conditions ET les offres » — est nul : il n'est pas libre, puisque
        refuser l'un empêche l'autre. D'où une case à part, facultative, qui
        n'entre pas dans la condition du bouton.
      */}
      <label className="flex items-start gap-3 px-1 cursor-pointer select-none">
        <input type="checkbox" checked={marketing}
               onChange={(e) => setMarketing(e.target.checked)}
               className="mt-0.5 size-4 shrink-0 rounded border-white/30 bg-white/10 accent-emerald-500" />
        <span className="text-xs text-white/60 leading-snug">
          Je veux recevoir les offres et actualités OGRYN par e-mail.
          Facultatif, et retirable à tout moment depuis mes paramètres.
        </span>
      </label>

      {err && <p className="text-sm text-rose-300 px-1">{err}</p>}

      <button type="submit" disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-black py-4 rounded-2xl text-base transition-colors">
        {busy ? 'Création…' : 'Créer mon compte'}
      </button>
    </form>
  );
}

type OngletConnexion = 'social' | 'telephone' | 'email' | 'cle';
const DERNIER_ONGLET_CLE = 'ogryn:dernier-mode-connexion';

export function FormulaireConnexion({ role, onForgot }: { role: Role; onForgot: () => void }) {
  /**
   * ONGLETS, PAS UN PAVÉ EMPILÉ (21/09, deuxième passe). Les quatre blocs
   * affichés en même temps débordaient encore de l'écran une fois resserrés
   * au maximum ; un seul panneau monté à la fois règle le problème à la
   * racine plutôt que de continuer à chercher de la place. Le balayage
   * tactile reprend le geste déjà éprouvé du carousel de rôle plus haut
   * (`onTouchStart`/`onTouchEnd`, seuil 40 px) — pas un nouveau geste inventé.
   */
  const onglets: { id: OngletConnexion; label: string; icon: React.ReactNode }[] = [
    // « Tiers », pas « Réseaux sociaux » : Google/Apple n'en sont pas (22/09).
    ...(SOCIAUX.length ? [{ id: 'social' as const, label: 'Tiers', icon: <Users size={18} aria-hidden /> }] : []),
    { id: 'telephone' as const, label: 'Téléphone', icon: <Phone size={18} aria-hidden /> },
    { id: 'email' as const, label: 'E-mail', icon: <Mail size={18} aria-hidden /> },
    { id: 'cle' as const, label: 'Clé d’accès', icon: <KeyRound size={18} aria-hidden /> },
  ];
  /* Mémorisé par appareil (22/09) : le dernier onglet choisi redevient l'onglet
     actif par défaut, au lieu de toujours rouvrir sur « Comptes ». */
  const [actif, setActif] = useState<OngletConnexion>(() => {
    if (typeof window === 'undefined') return onglets[0].id;
    const sauve = window.localStorage.getItem(DERNIER_ONGLET_CLE);
    return onglets.some(o => o.id === sauve) ? (sauve as OngletConnexion) : onglets[0].id;
  });
  useEffect(() => {
    try { window.localStorage.setItem(DERNIER_ONGLET_CLE, actif); } catch {}
  }, [actif]);

  const startX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) < 40) return;
    const i = onglets.findIndex(o => o.id === actif);
    if (dx < 0 && i < onglets.length - 1) setActif(onglets[i + 1].id);
    else if (dx > 0 && i > 0) setActif(onglets[i - 1].id);
  };

  const [email, setEmail] = useState('');
  const [mdp, setMdp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Erreur de la clé d'accès séparée de celle de l'e-mail : deux panneaux
      distincts, chacun garde son message. */
  const [errCle, setErrCle] = useState<string | null>(null);

  async function envoyer(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const { error } = await authClient.signIn.email({ email: email.trim(), password: mdp });
      if (error) throw { error };
    } catch (e2) {
      setErr(messageErreur(e2, 'Connexion impossible — réessaie.'));
      setBusy(false);
    }
  }

  /**
   * LA CLÉ D'ACCÈS EN DERNIER : c'est le facteur le plus sûr, mais aussi le
   * moins répandu — Google, le SMS et l'e-mail+mot de passe couvrent déjà
   * tout le monde. Toutes les options restent visibles en même temps (21/09,
   * demande explicite) : plus de bouton à déplier pour atteindre l'e-mail.
   */
  async function parCleDAcces() {
    if (busy) return;
    setBusy(true); setErrCle(null);
    try {
      const res = await authClient.signIn.passkey();
      if (res?.error) throw { error: res.error };
    } catch {
      setErrCle('Aucune clé d’accès reconnue sur cet appareil.');
      setBusy(false);
    }
  }

  return (
    <>
      {/* Le panneau seul défile (`flex-1 min-h-0 overflow-y-auto`) : titre
          (posé par la page appelante) et barre d'onglets ci-dessous restent à
          hauteur fixe quel que soit l'onglet actif — signalé le 22/09, la
          bascule sous le panneau ne suffisait pas seule, elle bougeait
          encore avec lui. */}
      <div data-slot="onglet-panneau" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
           key={actif} className="flex-1 min-h-0 overflow-y-auto flex flex-col" style={{ animation: 'ogentree .75s cubic-bezier(.4,0,.2,1)' }}>
        <div className="w-full space-y-3">
          {actif === 'social' && <BoutonsSociaux role={role} sansSeparateur />}

          {actif === 'telephone' && (
            <ConnexionTelephone role={role === 'prestataire' ? 'prestataire' : 'client'} />
          )}

          {actif === 'email' && (
            <form onSubmit={envoyer} className="space-y-3">
              <input className={CHAMP} type="email" autoComplete="email" required
                     inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                     placeholder="Adresse e-mail" value={email}
                     /* Le clavier mobile glisse volontiers une espace après une
                        suggestion : on la retire à la saisie plutôt que de refuser
                        la connexion pour un caractère invisible. */
                     onChange={e => setEmail(e.target.value.replace(/\s/g, ""))} />
              <ChampMotDePasse value={mdp} onChange={setMdp} autoComplete="current-password" />

              <div className="text-right -mt-1">
                <button type="button" onClick={onForgot} className="text-sm text-white/45 hover:text-white/70 font-semibold">
                  Mot de passe oublié ?
                </button>
              </div>

              {err && <p className="text-sm text-rose-300 px-1">{err}</p>}

              <button type="submit" disabled={busy}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-black py-4 rounded-2xl text-base transition-colors">
                {busy ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>
          )}

          {actif === 'cle' && (
            <>
              <button type="button" onClick={parCleDAcces} disabled={busy}
                      className="w-full flex items-center justify-center gap-2 bg-white/8 hover:bg-white/12 border border-white/12 disabled:opacity-50 text-white font-bold py-4 rounded-2xl text-base transition-colors">
                <KeyRound size={18} aria-hidden /> Utiliser ma clé d’accès
              </button>
              {errCle && <p className="text-sm text-rose-300 px-1">{errCle}</p>}
            </>
          )}
        </div>
      </div>

      {/* SlideToggle (components/segmented.tsx) : LA bascule à piste du
          bandeau de filtres prestataire (« Immédiat / Sur RDV », « Sur place
          / En garage ») — piste verte creusée, curseur du feuillet qui
          glisse, PAS `Segmented` (une confusion de ma part la première fois :
          `Segmented` est la bascule à bosses du filtre Tout/Diagnostics/
          Prioritaires, une toute autre pièce visuelle). Cet écran reste
          toujours sombre, hors du cycle jour/nuit géré par `data-theme` sur
          <html>, donc les jetons du thème nuit (globals.css
          `:root[data-theme='dark']`) sont reposés ici en local plutôt que de
          forcer data-theme sur toute la page.
          SOUS le panneau, pas au-dessus (22/09) : plus proche du pouce en
          bas de l'écran — demandé dès la 4e passe. `shrink-0` : ne rétrécit
          ni ne grandit jamais avec la hauteur du panneau voisin. */}
      <div data-slot="onglets-connexion" className="shrink-0 mt-4" style={{
        ['--sh-emboss' as unknown as string]: '3px 3px 8px rgba(0,0,0,0.55), -3px -3px 8px rgba(255,255,255,0.10)',
        ['--toggle-hollow' as unknown as string]: 'inset 3px 3px 9px rgba(0,0,0,0.55), inset -3px -3px 8px rgba(255,255,255,0.12)',
        ['--toggle-on-ink' as unknown as string]: '#C7F9E0',
        ['--toggle-on-glow' as unknown as string]: 'drop-shadow(0 0 5px rgb(52 211 153 / .85))',
        ['--toggle-off-ink' as unknown as string]: 'rgba(0,0,0,0.42)',
        ['--sh-card' as unknown as string]: '0 2px 3px rgba(0,0,0,0.60), 0 6px 10px rgba(0,0,0,0.50)',
        ['--s-sheet-rgb' as unknown as string]: '56 56 56',
      } as React.CSSProperties}>
        <SlideToggle<OngletConnexion>
          options={onglets.map(o => [o.id, o.label] as const)}
          value={actif}
          onChange={setActif}
          icons={Object.fromEntries(onglets.map(o => [o.id, o.icon]))}
          className="h-[58px]"
        />
      </div>
    </>
  );
}

/** Demande du lien de réinitialisation — un seul champ, une seule action. */
function FormulaireOubli({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [envoye, setEnvoye] = useState(false);

  async function envoyer(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      // `redirectTo` : Better Auth y ramène après avoir vérifié le jeton, en
      // ajoutant `?token=...` — c'est cette page qui lit le jeton plus bas.
      await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo: `${window.location.origin}/bienvenue`,
      });
      // Même message que l'e-mail existe ou non (Better Auth se comporte pareil
      // côté serveur) : révéler l'absence d'un compte serait une fuite en soi.
      setEnvoye(true);
    } catch (e2) {
      setErr(messageErreur(e2, 'Envoi impossible — réessaie.'));
    } finally {
      setBusy(false);
    }
  }

  if (envoye) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-white/70">
          Si un compte existe pour <span className="font-bold text-white">{email.trim()}</span>,
          un lien de réinitialisation vient de partir. Il est valable une heure.
        </p>
        <button type="button" onClick={onBack} className="text-sm font-bold text-emerald-400 hover:text-emerald-300">
          Retour à la connexion
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={envoyer} className="space-y-3">
      <input className={CHAMP} type="email" autoComplete="email" required
             inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
             placeholder="Adresse e-mail" value={email}
             onChange={e => setEmail(e.target.value.replace(/\s/g, ""))} />
      {err && <p className="text-sm text-rose-300 px-1">{err}</p>}
      <button type="submit" disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-black py-4 rounded-2xl text-base transition-colors">
        {busy ? 'Envoi…' : 'Envoyer le lien'}
      </button>
    </form>
  );
}

/** Pose le nouveau mot de passe — le jeton vient de l'URL (`?token=`), jamais tapé. */
function FormulaireReinitialisation({ onDone }: { onDone: () => void }) {
  const [mdp, setMdp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function envoyer(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) { setErr('Lien invalide ou expiré — redemande un e-mail.'); return; }
    setBusy(true); setErr(null);
    try {
      const { error } = await authClient.resetPassword({ newPassword: mdp, token });
      if (error) throw { error };
      onDone();
    } catch (e2) {
      setErr(messageErreur(e2, 'Réinitialisation impossible — le lien a peut-être expiré.'));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={envoyer} className="space-y-3">
      <ChampMotDePasse value={mdp} onChange={setMdp} autoComplete="new-password" placeholder="Nouveau mot de passe" minLength={MDP_MIN} />
      {err && <p className="text-sm text-rose-300 px-1">{err}</p>}
      <button type="submit" disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-black py-4 rounded-2xl text-base transition-colors">
        {busy ? 'Enregistrement…' : 'Valider le nouveau mot de passe'}
      </button>
    </form>
  );
}


// ── Icônes ──
function BoltIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function HandshakeIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m11 17 2 2a1 1 0 1 0 3-3" />
      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
      <path d="m21 3 1 11h-2" />
      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
      <path d="M3 4h8" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
function InboxIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
    </svg>
  );
}
function WalletIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
      <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
    </svg>
  );
}
function CarIcon({ small }: { small?: boolean }) {
  const s = small ? 14 : 30;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
      <circle cx="7" cy="17" r="2" /><path d="M9 17h6" /><circle cx="17" cy="17" r="2" />
    </svg>
  );
}
function WrenchIcon({ small }: { small?: boolean }) {
  const s = small ? 14 : 30;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}
